/**
 * Paddle.js loader + checkout opener.
 *
 * We avoid bundling Paddle.js (it's ~80 KB and only needed when a user
 * actually clicks the upgrade CTA). Loaded lazily on demand from Paddle's
 * CDN. Every checkout is INLINE — `openInlineCheckout` mounts Paddle's card
 * iframe inside our own PaymentFrame (the paywall's overlay checkout was
 * removed 2026-09-21).
 *
 * Env (Vite-baked):
 *   VITE_PADDLE_ENV          — 'sandbox' | 'production'
 *   VITE_PADDLE_CLIENT_TOKEN — Paddle "Client-side token" from dashboard
 *   VITE_PADDLE_PRICE_ID     — the $5/mo price id, 7-day trial (sandbox or live)
 *   VITE_PADDLE_PRICE_ID_RETURNING — the same $5/mo with NO trial, for people
 *                              who already had theirs (resubscribe). Without
 *                              it, an ended trial could start a new free week
 *                              on the same empty card (2026-10-18).
 *
 * The checkout includes `customData.user_id` so the webhook can route the
 * subscription to the right Supabase user — plus, on app.fluent.ge, the Meta
 * pixel cookies + user agent the webhook forwards to Meta (metaPixel.ts).
 */

import { metaCheckoutData, type CheckoutCompletedData } from '../lib/metaPixel.js';

interface PaddleEvent {
  data:
    | ({ transaction?: { id?: string; status?: string }; customer?: { id?: string } } & CheckoutCompletedData)
    | undefined;
  name: string;
}

interface PaddleCheckoutOptions {
  items?: Array<{ priceId: string; quantity: number }>;
  transactionId?: string;
  customer?: { email?: string; address?: { countryCode: string } };
  customData?: Record<string, string>;
  settings?: {
    displayMode?: 'overlay' | 'inline';
    theme?: 'light' | 'dark';
    locale?: string;
    successUrl?: string;
    frameTarget?: string;
    frameInitialHeight?: number;
    frameStyle?: string;
    showAddDiscounts?: boolean;
  };
}

interface PaddleSDK {
  Environment: { set: (env: 'sandbox' | 'production') => void };
  Initialize: (opts: { token: string; eventCallback?: (e: PaddleEvent) => void }) => void;
  Checkout: { open: (opts: PaddleCheckoutOptions) => void; close?: () => void };
}

declare global {
  interface Window {
    Paddle?: PaddleSDK;
  }
}

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

let loaderPromise: Promise<PaddleSDK> | null = null;

function loadPaddle(): Promise<PaddleSDK> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Paddle.js requires a browser'));
  }
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<PaddleSDK>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PADDLE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.Paddle) resolve(window.Paddle);
        else reject(new Error('Paddle.js loaded but window.Paddle is missing'));
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Paddle.js')));
      return;
    }
    const s = document.createElement('script');
    s.src = PADDLE_JS_URL;
    s.async = true;
    s.onload = () => {
      if (window.Paddle) resolve(window.Paddle);
      else reject(new Error('Paddle.js loaded but window.Paddle is missing'));
    };
    s.onerror = () => reject(new Error('Failed to load Paddle.js'));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

let initialized = false;

/**
 * Checkout event fan-out. Paddle.Initialize accepts exactly ONE global
 * eventCallback, but each mounted PaymentFrame (paywall, subscription
 * screen) wants to react to completion.
 * Register through here; unsubscribe on unmount.
 */
type PaddleEventListener = (e: PaddleEvent) => void;
const eventListeners = new Set<PaddleEventListener>();

export function onPaddleEvent(listener: PaddleEventListener): () => void {
  eventListeners.add(listener);
  return () => eventListeners.delete(listener);
}

async function ensureInitialized(): Promise<PaddleSDK> {
  const Paddle = await loadPaddle();
  if (initialized) return Paddle;

  const env = (import.meta.env.VITE_PADDLE_ENV as string | undefined) ?? 'sandbox';
  const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
  if (!token) {
    throw new Error(
      'VITE_PADDLE_CLIENT_TOKEN is missing — populate apps/app/.env.local from the workspace .env.local',
    );
  }
  if (env !== 'production') {
    Paddle.Environment.set('sandbox');
  }
  // Capture errors via eventCallback so the UI can surface a real message
  // instead of Paddle's generic "Something went wrong". Stash the last error
  // on `window` so /auth/debug-style introspection picks it up too.
  Paddle.Initialize({
    token,
    eventCallback: (e: PaddleEvent) => {
      if (e.name === 'checkout.error') {
        const w = window as Window & { __fluentPaddleLastError?: unknown };
        w.__fluentPaddleLastError = {
          name: e.name,
          data: e.data,
          at: new Date().toISOString(),
        };
        if (typeof console !== 'undefined') {
          console.error('[paddle] checkout.error', e.data);
        }
      }
      for (const listener of eventListeners) {
        try {
          listener(e);
        } catch {
          // One listener's throw must not starve the others.
        }
      }
    },
  });
  initialized = true;
  return Paddle;
}

/* ------------------------------------------------------------------ */
/*  Skipping Paddle's "enter your details" page                       */
/* ------------------------------------------------------------------ */

/**
 * Paddle opens on an English form asking for email + country unless BOTH are
 * passed up front ("Prefill checkout properties" in Paddle's docs). Ten
 * countries also require a postal code there, and we have no way to know it,
 * so for those we pass nothing and let Paddle ask as before — a wrong or
 * missing postal code is worse than one extra tap.
 *
 * List read off Paddle's supported-countries table (`uses_postal_code`),
 * 2026-09-22: every other country Paddle sells in needs only the country.
 */
const POSTAL_CODE_COUNTRIES = new Set(['AU', 'CA', 'DE', 'ES', 'FR', 'GB', 'IN', 'IT', 'NL', 'US']);

/** What to pass as `customer`, given the visitor's country (null if unknown). */
export function checkoutCustomer(
  email: string,
  country: string | null,
): { email: string; address?: { countryCode: string } } {
  if (!country || POSTAL_CODE_COUNTRIES.has(country)) return { email };
  return { email, address: { countryCode: country } };
}

let countryPromise: Promise<string | null> | null = null;

/** The visitor's country, from Cloudflare at the edge (/api/geo). Cached for
 *  the page's lifetime; any failure answers null, which just means Paddle
 *  asks for the country itself. */
export function resolveCheckoutCountry(): Promise<string | null> {
  countryPromise ??= (async () => {
    try {
      const res = await fetch('/api/geo', { headers: { accept: 'application/json' } });
      if (!res.ok) return null;
      const body: unknown = await res.json();
      const country = (body as { country?: unknown }).country;
      return typeof country === 'string' && /^[A-Z]{2}$/.test(country) ? country : null;
    } catch {
      // Offline, or a dev server with no Pages Functions. Not an error.
      return null;
    }
  })();
  return countryPromise;
}

/**
 * Inline checkout — the card form renders INSIDE our page, in a container
 * we style, instead of Paddle's branded overlay. This is how the
 * subscription screen keeps the user in our world: our headings, our
 * price summary, our buttons; Paddle is just the card fields.
 *
 * `frameTarget` is a CSS CLASS NAME (no dot) of an element that must
 * already be in the DOM when this is called.
 *
 * Two modes:
 *   - subscribe:  "default" args (new subscription); `returning: true` uses
 *                 the no-trial price (resubscribe)
 *   - card swap:  pass `transactionId` minted by /api/paddle-card-txn
 *                 (also collects the outstanding balance for past_due)
 */
export interface OpenInlineCheckoutArgs {
  frameTarget: string;
  userId: string;
  email: string;
  transactionId?: string;
  /** Already had a subscription (and so a trial): pay today, no free week. */
  returning?: boolean;
}

export async function openInlineCheckout(args: OpenInlineCheckoutArgs): Promise<void> {
  const Paddle = await ensureInitialized();
  const settings: NonNullable<PaddleCheckoutOptions['settings']> = {
    displayMode: 'inline',
    theme: 'light',
    locale: 'en',
    frameTarget: args.frameTarget,
    frameInitialHeight: 450,
    frameStyle: 'width: 100%; min-width: 286px; background-color: transparent; border: none;',
    showAddDiscounts: false,
  };
  if (args.transactionId) {
    Paddle.Checkout.open({ transactionId: args.transactionId, settings });
    return;
  }
  const priceId = checkoutPriceId(args.returning ?? false);
  Paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: checkoutCustomer(args.email, await resolveCheckoutCountry()),
    customData: { user_id: args.userId, ...metaCheckoutData() },
    settings,
  });
}

/** Which price a new checkout uses: the 7-day-trial one for a first
 *  subscription, the no-trial one for anyone who already had theirs. Throws
 *  when the build lacks it — a checkout must never silently fall back to the
 *  trial price. */
export function checkoutPriceId(returning: boolean): string {
  const priceId = (
    returning ? import.meta.env.VITE_PADDLE_PRICE_ID_RETURNING : import.meta.env.VITE_PADDLE_PRICE_ID
  ) as string | undefined;
  if (!priceId) {
    const name = returning ? 'VITE_PADDLE_PRICE_ID_RETURNING' : 'VITE_PADDLE_PRICE_ID';
    throw new Error(`${name} is missing — populate apps/app/.env.local from the workspace .env.local`);
  }
  return priceId;
}

/** Tear down an open (inline) checkout frame, e.g. when the user backs out. */
export async function closeCheckout(): Promise<void> {
  if (!window.Paddle || !initialized) return;
  try {
    window.Paddle.Checkout.close?.();
  } catch {
    // Closing a not-open checkout is a no-op, not an error.
  }
}
